import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { validatePassword } from '../../utils/security'
import AffiliateHeader from '../../components/affiliate/AffiliateHeader'
import {
  Surface, TextField, Label, InputGroup, Select, ListBox, Switch, Button, Alert, Chip,
} from '@heroui/react'
import UserIcon from '~icons/solar/user-bold'
import Lock from '~icons/solar/lock-password-bold'
import Bell from '~icons/solar/bell-bold'
import Shield from '~icons/solar/shield-check-bold'
import Save from '~icons/solar/diskette-bold'
import Eye from '~icons/solar/eye-bold'
import EyeOff from '~icons/solar/eye-closed-bold'
import Check from '~icons/solar/check-circle-bold'
import AlertCircle from '~icons/solar/danger-circle-bold'
import Laptop from '~icons/solar/laptop-bold'
import Trash from '~icons/solar/trash-bin-trash-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

type SettingsTab = 'profile' | 'password' | 'notifications' | 'security'

interface NotificationSettings {
  emailSignups: boolean
  emailPurchases: boolean
  emailPayouts: boolean
  inAppSignups: boolean
  inAppPurchases: boolean
  inAppPayouts: boolean
  frequency: 'instant' | 'daily' | 'weekly'
}

export default function SettingsPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Profile state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    country: ''
  })

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  // Notification settings
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailSignups: true,
    emailPurchases: true,
    emailPayouts: true,
    inAppSignups: true,
    inAppPurchases: true,
    inAppPayouts: true,
    frequency: 'instant'
  })

  const showMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setSuccess(message)
      setError(null)
    } else {
      setError(message)
      setSuccess(null)
    }
    setTimeout(() => {
      setSuccess(null)
      setError(null)
    }, 5000)
  }

  const handleProfileUpdate = async () => {
    setSaving(true)

    // TODO: Replace with actual API call
    // await fetch(`/api/affiliates/${user.id}/profile`, {
    //   method: 'PUT',
    //   body: JSON.stringify(profileData)
    // })

    await new Promise(resolve => setTimeout(resolve, 1000))

    setSaving(false)
    showMessage('success', 'Profile updated successfully!')
  }

  const handlePasswordChange = async () => {
    // SECURITY: Validate password confirmation match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New passwords do not match')
      return
    }

    // SECURITY: Comprehensive password strength validation
    const validation = validatePassword(passwordData.newPassword)
    if (!validation.isValid) {
      showMessage('error', validation.feedback.join('. '))
      return
    }

    setSaving(true)

    // TODO: Replace with actual API call
    // In production:
    // 1. Send current password + new password to backend
    // 2. Backend verifies current password matches hash in database
    // 3. Backend hashes new password (bcrypt with salt rounds >= 12)
    // 4. Backend updates password hash
    // 5. Backend invalidates all existing sessions (force re-login)
    // 6. Backend sends password change confirmation email
    // await fetch(`/api/affiliates/${user.id}/password`, {
    //   method: 'PUT',
    //   body: JSON.stringify({
    //     currentPassword: passwordData.currentPassword,
    //     newPassword: passwordData.newPassword
    //   })
    // })

    await new Promise(resolve => setTimeout(resolve, 1000))

    setSaving(false)
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    showMessage('success', 'Password changed successfully!')
  }

  const handleNotificationUpdate = async () => {
    setSaving(true)

    // TODO: Replace with actual API call
    // await fetch(`/api/affiliates/${user.id}/notifications`, {
    //   method: 'PUT',
    //   body: JSON.stringify(notifications)
    // })

    await new Promise(resolve => setTimeout(resolve, 1000))

    setSaving(false)
    showMessage('success', 'Notification preferences updated!')
  }

  const tabs = [
    { id: 'profile' as SettingsTab, label: 'Profile', icon: UserIcon },
    { id: 'password' as SettingsTab, label: 'Password', icon: Lock },
    { id: 'notifications' as SettingsTab, label: 'Notifications', icon: Bell },
    { id: 'security' as SettingsTab, label: 'Security', icon: Shield }
  ]

  const countries = [
    { id: 'US', label: 'United States' },
    { id: 'CA', label: 'Canada' },
    { id: 'GB', label: 'United Kingdom' },
    { id: 'AU', label: 'Australia' },
    { id: 'DE', label: 'Germany' },
    { id: 'FR', label: 'France' },
    { id: 'Other', label: 'Other' },
  ]

  const emailNotifications: Array<{ key: keyof NotificationSettings; label: string; desc: string }> = [
    { key: 'emailSignups', label: 'New Signups', desc: 'Get notified when someone signs up using your link' },
    { key: 'emailPurchases', label: 'Purchases & Commissions', desc: 'Get notified when you earn a commission' },
    { key: 'emailPayouts', label: 'Payouts', desc: 'Get notified about payment status updates' },
  ]

  const inAppNotifications: Array<{ key: keyof NotificationSettings; label: string; desc: string }> = [
    { key: 'inAppSignups', label: 'New Signups', desc: 'Show in-app notifications for signups' },
    { key: 'inAppPurchases', label: 'Purchases & Commissions', desc: 'Show in-app notifications for commissions' },
    { key: 'inAppPayouts', label: 'Payouts', desc: 'Show in-app notifications for payouts' },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <AffiliateHeader />

      {/* Success/Error Messages */}
      {(success || error) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {success && (
            <Alert status="success" className="bg-success-soft">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Description className="text-success-soft-foreground">{success}</Alert.Description>
              </Alert.Content>
            </Alert>
          )}
          {error && (
            <Alert status="danger" className="bg-danger-soft">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
              </Alert.Content>
            </Alert>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64">
            <Surface variant="default" className="rounded-xl p-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-accent-soft text-accent-soft-foreground'
                      : 'text-muted hover:bg-black/5 dark:hover:bg-white/10'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </Surface>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <Surface variant="default" className="rounded-xl">

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-6">Profile Information</h2>

                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TextField
                        value={profileData.name}
                        onChange={(v) => setProfileData({ ...profileData, name: v })}
                      >
                        <Label>Full Name</Label>
                        <InputGroup variant="secondary">
                          <InputGroup.Input />
                        </InputGroup>
                      </TextField>

                      <TextField
                        type="email"
                        value={profileData.email}
                        onChange={(v) => setProfileData({ ...profileData, email: v })}
                      >
                        <Label>Email Address</Label>
                        <InputGroup variant="secondary">
                          <InputGroup.Input />
                        </InputGroup>
                      </TextField>
                    </div>

                    <TextField isDisabled value={user?.affiliateCode || ''}>
                      <Label>Affiliate Code (Read-only)</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Input />
                      </InputGroup>
                    </TextField>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TextField
                        type="tel"
                        value={profileData.phone}
                        onChange={(v) => setProfileData({ ...profileData, phone: v })}
                      >
                        <Label>Phone Number (Optional)</Label>
                        <InputGroup variant="secondary">
                          <InputGroup.Input placeholder="+1 (555) 123-4567" />
                        </InputGroup>
                      </TextField>

                      <Select
                        aria-label="Country"
                        variant="secondary"
                        value={profileData.country}
                        onChange={(v) => setProfileData({ ...profileData, country: v as string })}
                      >
                        <Label>Country (Optional)</Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="" textValue="Select country...">
                              Select country...
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            {countries.map((c) => (
                              <ListBox.Item key={c.id} id={c.id} textValue={c.label}>
                                {c.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    </div>
                  </div>

                  <Button onPress={handleProfileUpdate} isDisabled={saving}>
                    {saving ? <LoadingSpinner className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}

              {/* Password Tab */}
              {activeTab === 'password' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-6">Change Password</h2>

                  <div className="space-y-4 max-w-md mb-6">
                    <TextField
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(v) => setPasswordData({ ...passwordData, currentPassword: v })}
                    >
                      <Label>Current Password</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Prefix>
                          <Lock className="h-4 w-4" />
                        </InputGroup.Prefix>
                        <InputGroup.Input />
                        <InputGroup.Suffix>
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            className="text-muted hover:text-foreground transition-colors"
                          >
                            {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </InputGroup.Suffix>
                      </InputGroup>
                    </TextField>

                    <TextField
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(v) => setPasswordData({ ...passwordData, newPassword: v })}
                    >
                      <Label>New Password</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Prefix>
                          <Lock className="h-4 w-4" />
                        </InputGroup.Prefix>
                        <InputGroup.Input />
                        <InputGroup.Suffix>
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            className="text-muted hover:text-foreground transition-colors"
                          >
                            {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </InputGroup.Suffix>
                      </InputGroup>
                      <p className="text-xs text-muted mt-1">At least 8 characters</p>
                    </TextField>

                    <TextField
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(v) => setPasswordData({ ...passwordData, confirmPassword: v })}
                    >
                      <Label>Confirm New Password</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Prefix>
                          <Lock className="h-4 w-4" />
                        </InputGroup.Prefix>
                        <InputGroup.Input />
                        <InputGroup.Suffix>
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            className="text-muted hover:text-foreground transition-colors"
                          >
                            {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </InputGroup.Suffix>
                      </InputGroup>
                    </TextField>
                  </div>

                  <Button
                    onPress={handlePasswordChange}
                    isDisabled={saving || !passwordData.currentPassword || !passwordData.newPassword}
                  >
                    {saving ? <LoadingSpinner className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    {saving ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-6">Notification Preferences</h2>

                  <div className="space-y-6 mb-6">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">Email Notifications</h3>
                      <div className="space-y-3">
                        {emailNotifications.map(item => (
                          <Surface key={item.key} variant="secondary" className="rounded-lg p-3">
                            <Switch
                              isSelected={notifications[item.key] as boolean}
                              onChange={(v) => setNotifications({ ...notifications, [item.key]: v })}
                            >
                              <Switch.Control>
                                <Switch.Thumb />
                              </Switch.Control>
                              <Switch.Content>
                                <Label>{item.label}</Label>
                                <p className="text-sm text-muted">{item.desc}</p>
                              </Switch.Content>
                            </Switch>
                          </Surface>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">In-App Notifications</h3>
                      <div className="space-y-3">
                        {inAppNotifications.map(item => (
                          <Surface key={item.key} variant="secondary" className="rounded-lg p-3">
                            <Switch
                              isSelected={notifications[item.key] as boolean}
                              onChange={(v) => setNotifications({ ...notifications, [item.key]: v })}
                            >
                              <Switch.Control>
                                <Switch.Thumb />
                              </Switch.Control>
                              <Switch.Content>
                                <Label>{item.label}</Label>
                                <p className="text-sm text-muted">{item.desc}</p>
                              </Switch.Content>
                            </Switch>
                          </Surface>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">Email Frequency</h3>
                      <Select
                        aria-label="Email frequency"
                        variant="secondary"
                        className="max-w-xs"
                        value={notifications.frequency}
                        onChange={(v) => setNotifications({ ...notifications, frequency: v as 'instant' | 'daily' | 'weekly' })}
                      >
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="instant" textValue="Instant (as they happen)">
                              Instant (as they happen)
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item id="daily" textValue="Daily Digest">
                              Daily Digest
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item id="weekly" textValue="Weekly Summary">
                              Weekly Summary
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    </div>
                  </div>

                  <Button onPress={handleNotificationUpdate} isDisabled={saving}>
                    {saving ? <LoadingSpinner className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-6">Security Settings</h2>

                  <div className="space-y-6">
                    <Surface variant="secondary" className="rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-foreground">Two-Factor Authentication</h3>
                          <p className="text-sm text-muted mt-1">Add an extra layer of security to your account</p>
                        </div>
                        <Chip color="warning" size="sm">Coming Soon</Chip>
                      </div>
                    </Surface>

                    <Surface variant="secondary" className="rounded-lg p-4">
                      <h3 className="font-medium text-foreground mb-2">Active Sessions</h3>
                      <p className="text-sm text-muted mb-3">You are currently signed in on these devices</p>
                      <Surface variant="default" className="rounded-lg p-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-surface-secondary rounded-lg">
                            <Laptop className="h-4 w-4 text-muted" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">Current Session</p>
                            <p className="text-xs text-muted">Windows, Chrome, {new Date().toLocaleString()}</p>
                          </div>
                        </div>
                        <Chip color="success" size="sm">Active</Chip>
                      </Surface>
                    </Surface>

                    <Surface className="rounded-lg p-4 bg-danger-soft">
                      <h3 className="font-medium text-danger-soft-foreground mb-2">Danger Zone</h3>
                      <p className="text-sm text-danger-soft-foreground/80 mb-3">Permanently delete your affiliate account and all data</p>
                      <Button variant="outline" className="text-danger border-danger/30 hover:bg-danger-soft">
                        <Trash className="h-4 w-4" />
                        Delete Account
                      </Button>
                    </Surface>
                  </div>
                </div>
              )}

            </Surface>
          </div>
        </div>
      </main>
    </div>
  )
}
